import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, MapPin, Clock, ArrowRight, Ticket, Sparkles } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get full image URL
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/uploads')) return `${BASE_URL}/api${url}`;
  return url;
};

const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mcSettings, setMcSettings] = useState(null);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axios.get(`${API}/events`);
        // Filter out archived events and sort by date
        const activeEvents = response.data.filter(e => !e.is_archived);
        setEvents(activeEvents);
      } catch (error) {
        console.error("Error fetching events:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
    // Pull Market Camp settings so the featured card always reflects current pricing/age/address
    axios.get(`${API}/public/market-camp-settings`)
      .then(r => setMcSettings(r.data))
      .catch(() => {});
  }, []);

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    // Parse date as local time to avoid timezone shift
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateShort = (dateStr) => {
    if (!dateStr) return { month: '', day: '' };
    // Parse date as local time to avoid timezone shift
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return {
      month: date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
      day: date.getDate()
    };
  };

  // Dynamic Market Camp values for the featured card
  const mcAgeLabel = mcSettings?.age_range_label || "Ages 4–16";
  const mcLocation = mcSettings?.address_line || mcSettings?.location_label || "11560 149 St NW, Edmonton, AB";
  const mcDates = mcSettings?.dates_label || "Aug 13–15, 2026";
  const mcPrice = mcSettings?.price_regular || 139;

  return (
    <main className="pt-20" data-testid="events-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 bg-[#050505]">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-gradient-to-b from-[#D4AF37]/5 to-transparent" />
        </div>
        
        <div className="container-luxury relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="inline-block bg-[#D4AF37] text-[#050505] px-4 py-1 text-xs uppercase tracking-widest font-semibold mb-6">
              Upcoming Events
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-['Playfair_Display'] font-bold mb-6 text-[#F5F5F0]">
              Our Events
            </h1>
            <p className="text-[#A1A1AA] text-lg leading-relaxed">
              Join us for unforgettable experiences featuring premium mocktails, 
              live entertainment, and the finest atmosphere Edmonton has to offer.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Events Grid */}
      <section className="section-padding bg-[#0F0F0F]">
        <div className="container-luxury">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="spinner" />
            </div>
          ) : events.length === 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Featured: Mini Makers Market Camp (Registration) */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="group"
                data-testid="event-card-mini-makers-empty"
              >
                <Link to="/mini-makers-market-camp" className="block">
                  <div className="luxury-card overflow-hidden hover:border-[#D4AF37] transition-all duration-300">
                    <div className="relative h-56 overflow-hidden bg-gradient-to-br from-[#3F1A66] via-[#5B2A86] to-[#3F1A66] flex items-center justify-center p-4">
                      <img
                        src="https://customer-assets.emergentagent.com/job_db2be5c0-fcfc-48d7-8252-f22b445a2705/artifacts/uqf9sd9c_LOGO%20FOR%20MINI%20MAKERS.png"
                        alt="Mini Makers Market Camp"
                        className="max-h-full max-w-full object-contain drop-shadow-2xl transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/40 via-transparent to-transparent pointer-events-none" />
                      <div className="absolute top-4 left-4 bg-[#050505]/90 border border-[#D4AF37] p-3 text-center min-w-[60px]">
                        <span className="block text-[#D4AF37] text-xs font-bold tracking-wider">AUG</span>
                        <span className="block text-[#F5F5F0] text-xl font-['Playfair_Display'] font-bold">13–15</span>
                      </div>
                      <div className="absolute top-4 right-4">
                        <span className="bg-[#F5C518] text-[#3F1A66] px-3 py-1 text-xs uppercase tracking-widest font-bold">
                          Now Open
                        </span>
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#F5F5F0] mb-2 group-hover:text-[#D4AF37] transition-colors">
                        Mini Makers Market Camp
                      </h3>
                      <p className="text-[#D4AF37] text-sm font-['Cormorant_Garamond'] italic mb-3">
                        "Create it. Brand it. Showcase it."
                      </p>
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center gap-2 text-[#A1A1AA] text-sm">
                          <Sparkles className="w-4 h-4 text-[#D4AF37]" />
                          <span>Boys &amp; Girls · {mcAgeLabel}</span>
                        </div>
                        <div className="flex items-center gap-2 text-[#A1A1AA] text-sm">
                          <MapPin className="w-4 h-4 text-[#D4AF37]" />
                          <span className="truncate">{mcLocation} · {mcDates}</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between pt-4 border-t border-[rgba(212,175,55,0.15)]">
                        <span className="text-[#F5F5F0] font-semibold text-sm">From ${mcPrice}/child</span>
                        <span className="inline-flex items-center gap-1 text-[#D4AF37] text-sm group-hover:gap-2 transition-all">
                          Register Now
                          <ArrowRight className="w-4 h-4" />
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Featured: Mini Makers Market Camp (Registration) */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="group"
                data-testid="event-card-mini-makers"
              >
                <Link to="/mini-makers-market-camp" className="block">
                  <div className="luxury-card overflow-hidden hover:border-[#D4AF37] transition-all duration-300">
                    <div className="relative h-56 overflow-hidden bg-gradient-to-br from-[#3F1A66] via-[#5B2A86] to-[#3F1A66] flex items-center justify-center p-4">
                      <img
                        src="https://customer-assets.emergentagent.com/job_db2be5c0-fcfc-48d7-8252-f22b445a2705/artifacts/uqf9sd9c_LOGO%20FOR%20MINI%20MAKERS.png"
                        alt="Mini Makers Market Camp"
                        className="max-h-full max-w-full object-contain drop-shadow-2xl transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/40 via-transparent to-transparent pointer-events-none" />

                      <div className="absolute top-4 left-4 bg-[#050505]/90 border border-[#D4AF37] p-3 text-center min-w-[60px]">
                        <span className="block text-[#D4AF37] text-xs font-bold tracking-wider">AUG</span>
                        <span className="block text-[#F5F5F0] text-xl font-['Playfair_Display'] font-bold">13–15</span>
                      </div>

                      <div className="absolute top-4 right-4">
                        <span className="bg-[#F5C518] text-[#3F1A66] px-3 py-1 text-xs uppercase tracking-widest font-bold">
                          Now Open
                        </span>
                      </div>
                    </div>

                    <div className="p-6">
                      <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#F5F5F0] mb-2 group-hover:text-[#D4AF37] transition-colors">
                        Mini Makers Market Camp
                      </h3>
                      <p className="text-[#D4AF37] text-sm font-['Cormorant_Garamond'] italic mb-3">
                        "Create it. Brand it. Showcase it."
                      </p>

                      <div className="space-y-2 mb-4">
                        <div className="flex items-center gap-2 text-[#A1A1AA] text-sm">
                          <Sparkles className="w-4 h-4 text-[#D4AF37]" />
                          <span>Boys &amp; Girls · {mcAgeLabel}</span>
                        </div>
                        <div className="flex items-center gap-2 text-[#A1A1AA] text-sm">
                          <MapPin className="w-4 h-4 text-[#D4AF37]" />
                          <span className="truncate">{mcLocation} · {mcDates}</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-4 border-t border-[rgba(212,175,55,0.15)]">
                        <span className="text-[#F5F5F0] font-semibold text-sm">
                          From ${mcPrice}/child
                        </span>
                        <span className="inline-flex items-center gap-1 text-[#D4AF37] text-sm group-hover:gap-2 transition-all">
                          Register Now
                          <ArrowRight className="w-4 h-4" />
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>

              {events.map((event, index) => {
                const dateInfo = formatDateShort(event.date);
                
                return (
                  <motion.div
                    key={event.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="group"
                    data-testid={`event-card-${event.id}`}
                  >
                    <Link to={`/events/${event.id}`} className="block">
                      <div className="luxury-card overflow-hidden hover:border-[#D4AF37] transition-all duration-300">
                        {/* Event Image */}
                        <div className="relative h-56 overflow-hidden">
                          <img
                            src={getImageUrl(event.banner_image) || "https://images.unsplash.com/photo-1551024709-8f23befc6f87?w=800"}
                            alt={event.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
                          
                          {/* Date Badge */}
                          <div className="absolute top-4 left-4 bg-[#050505]/90 border border-[#D4AF37] p-3 text-center min-w-[60px]">
                            <span className="block text-[#D4AF37] text-xs font-bold tracking-wider">
                              {dateInfo.month}
                            </span>
                            <span className="block text-[#F5F5F0] text-2xl font-['Playfair_Display'] font-bold">
                              {dateInfo.day}
                            </span>
                          </div>

                          {/* Category Badge */}
                          {event.category && (
                            <div className="absolute top-4 right-4">
                              <span className="bg-[#D4AF37] text-[#050505] px-3 py-1 text-xs uppercase tracking-widest font-semibold">
                                {event.category}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Event Info */}
                        <div className="p-6">
                          <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#F5F5F0] mb-2 group-hover:text-[#D4AF37] transition-colors">
                            {event.title}
                          </h3>
                          
                          {event.tagline && (
                            <p className="text-[#D4AF37] text-sm font-['Cormorant_Garamond'] italic mb-3">
                              "{event.tagline}"
                            </p>
                          )}

                          <div className="space-y-2 mb-4">
                            <div className="flex items-center gap-2 text-[#A1A1AA] text-sm">
                              <Clock className="w-4 h-4 text-[#D4AF37]" />
                              <span>{event.time}</span>
                            </div>
                            <div className="flex items-center gap-2 text-[#A1A1AA] text-sm">
                              <MapPin className="w-4 h-4 text-[#D4AF37]" />
                              <span className="truncate">{event.location}</span>
                            </div>
                          </div>

                          {/* Ticket Info */}
                          {event.ticket_options && event.ticket_options.length > 0 && (
                            <div className="flex items-center justify-between pt-4 border-t border-[rgba(212,175,55,0.15)]">
                              <span className="text-[#F5F5F0] font-semibold">
                                From C${Math.min(...event.ticket_options.map(t => t.price))}
                              </span>
                              <span className="inline-flex items-center gap-1 text-[#D4AF37] text-sm group-hover:gap-2 transition-all">
                                View Details
                                <ArrowRight className="w-4 h-4" />
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Host Your Own Section */}
      <section className="section-padding bg-[#050505] border-t border-[rgba(212,175,55,0.15)]" data-testid="host-section">
        <div className="container-luxury">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="aspect-[4/3] overflow-hidden"
            >
              <img
                src="https://images.unsplash.com/photo-1712314947762-039576e58bc6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxlbGVnYW50JTIwZXZlbnQlMjBzZXR1cCUyMHdlZGRpbmclMjByZWNlcHRpb24lMjBuaWdodHxlbnwwfHx8fDE3NzIzNTM1ODV8MA&ixlib=rb-4.1.0&q=85"
                alt="Host your own event"
                className="w-full h-full object-cover"
              />
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={{ duration: 0.6 }}
            >
              <p className="text-[#D4AF37] uppercase tracking-[0.2em] text-sm mb-4">
                Private Events
              </p>
              <h2 className="text-3xl sm:text-4xl font-['Playfair_Display'] font-bold mb-6 text-[#F5F5F0]">
                Host Your Own Event
              </h2>
              <div className="gold-divider" />
              <p className="text-[#A1A1AA] leading-relaxed mb-6">
                Want to bring the Mix & Mingle experience to your own celebration? 
                From weddings to corporate galas, we offer full-service beverage 
                catering that transforms any event into an unforgettable experience.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/experiences" 
                  className="btn-primary inline-flex items-center justify-center gap-2"
                  data-testid="view-experiences-btn"
                >
                  <Sparkles className="w-5 h-5" />
                  View Experiences
                </Link>
                <Link 
                  to="/contact" 
                  className="btn-secondary text-center"
                  data-testid="contact-events-btn"
                >
                  Contact Us
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default EventsPage;
